import api from "@/app/api";
import Guid from "@/app/types/common/guid";
import { Instant, LocalDate, LocalTime } from "@js-joda/core";
import {ModelObject, ModelObjectWithDirtyCheck, type ValidationRule} from "@/app/services/modelObject";
import ServiceBase from "@/app/services/serviceBase";
import converters from "@/app/services/converters"

// Model Objects

export class TaskTypeReadOnly extends ModelObject<TaskTypeReadOnly> {

    /**
    * Sequence for ordering task types.
    */
    public sequence: number = 0;

    public constructor(jsonSerializedObject?: any) {
        super();
        if (jsonSerializedObject) {
          this.sequence = jsonSerializedObject.sequence;
        }

    }

    public toJsonSerializedObject(): any {
        const toRet = {} as any;
        toRet.sequence = this.sequence;
        return toRet;
    }

    public toProto(): any {
      return this.toJsonSerializedObject();
    }

    public toBinary(): Uint8Array {
      return new TextEncoder().encode(JSON.stringify(this.toJsonSerializedObject()));
    }

    public validationRules: ValidationRule = [
            {field: "sequence", rule: (v: any) => v == null || v >= -2147483648 || "Sequence is not greater than minimum allowed value: -2147483648"},
            {field: "sequence", rule: (v: any) => v == null || v <= 2147483647 || "Sequence is above maximum allowed value: 2147483647"},
    ];
}


export class TaskType extends ModelObjectWithDirtyCheck<TaskType> {

    public version: number = 0;
    public id: Guid = Guid.createEmpty();
    /**
    * Task Type Name
    * Not Nullable
    * Not Empty
    * Max Length : 1000
    */
    public name: string = "";
    public isHidden: boolean = false;
    /**
    * Duration of this task type in minutes.
    * Minimum : 1
    */
    public durationMinutes: number = 0;
    public readOnly: TaskTypeReadOnly = new TaskTypeReadOnly();

    public constructor(jsonSerializedObject?: any) {
        super();
        if (jsonSerializedObject) {
          this.version = jsonSerializedObject.version;
          this.id = Guid.parseOrEmpty(jsonSerializedObject.id);
          this.name = jsonSerializedObject.name;
          this.isHidden = jsonSerializedObject.isHidden;
          this.durationMinutes = jsonSerializedObject.durationMinutes;
          this.readOnly = new TaskTypeReadOnly(jsonSerializedObject.readOnly);
        }

        this.setDirtyCheckpoint();
    }

    public toJsonSerializedObject(): any {
        const toRet = {} as any;
        toRet.version = this.version;
        toRet.id = this.id.toString();
        toRet.name = this.name;
        toRet.isHidden = this.isHidden;
        toRet.durationMinutes = this.durationMinutes;
        toRet.readOnly = this.readOnly.toJsonSerializedObject();
        return toRet;
    }

    public toProto(): any {
      return this.toJsonSerializedObject();
    }

    public toBinary(): Uint8Array {
      return new TextEncoder().encode(JSON.stringify(this.toJsonSerializedObject()));
    }

    public validationRules: ValidationRule = [
            {field: "version", rule: (v: any) => v == null || v >= -9223372036854775808 || "Version is not greater than minimum allowed value: -9223372036854775808"},
            {field: "version", rule: (v: any) => v == null || v <= 9223372036854775807 || "Version is above maximum allowed value: 9223372036854775807"},
            {field: "id", rule: (v: any) => !v.isEmpty() || "TaskType.Id cannot be empty"},
            {field: "name", rule: (v: any) => v == null || v.length !== 0 || "Name cannot be empty"},
            {field: "name", rule: (v: any) => v == null || v.length <= 1000 || "Name has too many characters (1000)"},
            {field: "durationMinutes", rule: (v: any) => v == null || v >= 1 || "DurationMinutes is not greater than minimum allowed value: 1"},
            {field: "durationMinutes", rule: (v: any) => v == null || v <= 2147483647 || "DurationMinutes is above maximum allowed value: 2147483647"},
            {field: "readOnly", rule: (v: any) => !!v || "readOnly is required"},
    ];
}


export class TaskTypeService extends ServiceBase {

  public async getTaskTypes(taskTypeIds: readonly Guid[], opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<(TaskType)[]> {
    const req = {
    tenantId:api.tenantId.toString(),
    taskTypeIds:taskTypeIds.map(v => v.toString()),
    };
    const extractor = (response:any) => response.taskTypes.map((v: any) => new TaskType(v));;
    return this.makeJsonRequest("taskType/v1/getTaskTypes", req, false, extractor, opts);
  };

  public async getTaskType(taskTypeId: Guid, opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<TaskType> {
    const req = {
    tenantId:api.tenantId.toString(),
    taskTypeId:taskTypeId.toString(),
    };
    const extractor = (response:any) => new TaskType(response.taskType);;
    return this.makeJsonRequest("taskType/v1/getTaskType", req, false, extractor, opts);
  };

  public async getAllTaskTypes(opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<(TaskType)[]> {
    const req = {
    tenantId:api.tenantId.toString(),
    };
    const extractor = (response:any) => response.taskTypes.map((v: any) => new TaskType(v));;
    return this.makeJsonRequest("taskType/v1/getAllTaskTypes", req, false, extractor, opts);
  };

  public async saveTaskType(taskType: TaskType, opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<void> {
    const req = {
    tenantId:api.tenantId.toString(),
    taskType:taskType.toJsonSerializedObject(),
    };
    const extractor = null;
    return this.makeJsonRequest("taskType/v1/saveTaskType", req, true, extractor, opts);
  };

  public async setTaskTypeIsHidden(taskTypeId: Guid, hide: boolean, opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<void> {
    const req = {
    tenantId:api.tenantId.toString(),
    taskTypeId:taskTypeId.toString(),
    hide:hide,
    };
    const extractor = null;
    return this.makeJsonRequest("taskType/v1/setTaskTypeIsHidden", req, true, extractor, opts);
  };

  public async updateTaskTypesSequence(taskTypeIds: readonly Guid[], opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<void> {
    const req = {
    tenantId:api.tenantId.toString(),
    taskTypeIds:taskTypeIds.map(v => v.toString()),
    };
    const extractor = null;
    return this.makeJsonRequest("taskType/v1/updateTaskTypesSequence", req, true, extractor, opts);
  };
}

export const taskTypeService = new TaskTypeService();